import { Backdrop, Button, CircularProgress, Paper, styled } from "@mui/material";
import cross from "components/icons/cross-black.svg";
import UploadSearch from "../../upload/UploadSearch";
import profile_icon from "../../icons/profile_gray.svg";
import info_icon from "../../icons/Information_Circle.svg";
import { useDispatch, useSelector } from "react-redux";
import { IAppState } from "../../../store/app-state";
import { IGif } from "services/types";
import UploadNicheItem from "../../upload/UploadNicheItem";
import { Fragment, useEffect, useState } from "react";
import { addToNiche } from "../../../store/niches/niches-action";
import colors from "../../../theme/themeConstants";
import { useIsAuth } from "../../../hooks/useIsAuth";
import { useKeyPress } from "../../../hooks/useKeyPress";
import tick from "../../icons/tick.svg";
import "./AddToNichePopup.scss";
import { useMyNichesQuery } from "queries";
import { showAuthModal } from "store/application/application-action";

interface IProps {
  isOpen: boolean;
  togglePopup: () => void;
  gif: IGif;
}

const AddToNichePopup = (props: IProps) => {
  const { isOpen, togglePopup, gif } = props;

  const niches = useMyNichesQuery({ previews: true }).data?.niches;

  const isLoading = useSelector((state: IAppState) => state.niches.loading);
  const [submitClicked, setSubmitClicked] = useState(false);

  const isAuth = useIsAuth();

  const [filteredNiches, setFilteredNiches] = useState<any>(niches);
  const [selectedNiches, setSelectedNiches] = useState<any>([]);

  const escPressed = useKeyPress("Escape");

  const dispatch = useDispatch();

  const stopBackdropPropagation = (event: React.MouseEvent) => event.stopPropagation();

  const search = (value: string) => {
    if (value && niches) {
      const newNiches = niches.filter((el: any) => el.name.toLowerCase().includes(value.toLowerCase()));
      setFilteredNiches(newNiches);
    } else {
      setFilteredNiches(niches);
    }
  };

  const handleSelectNiche = (niche: any) => {
    const newSelectedNiches = [...selectedNiches];
    let isExist = false;

    newSelectedNiches.forEach((el, index) => {
      if (el === niche.id) {
        isExist = true;
        newSelectedNiches.splice(index, 1);
      }
    });

    if (!isExist && newSelectedNiches.length < 3) {
      newSelectedNiches.push(niche.id);
    }
    setSelectedNiches(newSelectedNiches);
  };

  const onSubmit = () => {
    selectedNiches.forEach((el: any) => {
      dispatch(addToNiche({
        niche_id: el,
        gif_id: gif.id
      }));
      setSubmitClicked(true);
    });
  };

  const handleLogin = () => {
    dispatch(showAuthModal());
  };

  useEffect(() => {
    if (submitClicked && !isLoading) {
      togglePopup();
    }
    // eslint-disable-next-line
  }, [submitClicked, isLoading])

  useEffect(() => {
    if (escPressed) {
      // @ts-ignore
      togglePopup();
    }
    // eslint-disable-next-line
  }, [escPressed])

  useEffect(() => {
    if (niches) {
      setFilteredNiches(niches);
    }
  }, [niches]);

  return (
    <Backdrop
      open={isOpen}
      sx={{
        zIndex: "var(--popupZ)"
      }}
      onClick={stopBackdropPropagation}
    >
      <Paper
        className="addToNichePopup"
        sx={{
          maxHeight: "90vh",
          maxWidth: "420px",
          width: "100%",
          padding: "2rem",
          borderRadius: "24px",
          zIndex: 22,
          display: "flex",
          flexDirection: "column",
          "@media screen and (max-width: 767px)": {
            maxHeight: "95vh",
            maxWidth: "90%",
            padding: "1rem"
          },
          "& input": {
            color: "#1D1D1D !important"
          }
        }}
      >
        <PopupSubheader>
          <h3>Add to Niche</h3>
          <Button onClick={togglePopup}><img src={cross} alt="close popup" /></Button>
        </PopupSubheader>
        {isAuth ? (
          isLoading ? <CircularProgress sx={{ color: colors.redPink, margin: "auto" }}/>
            : (
              <Fragment>
                <SearchContainer>
                  <UploadSearch onSearch={search}/>
                </SearchContainer>
                <InfoBlock>
                  <img src={info_icon} alt="info"/>
                  <p>You can add content to niche if it hasn't been published there before.</p>
                </InfoBlock>
                <NichesWrapper className="nicheList">
                  {filteredNiches ? filteredNiches.map((el: any) => (
                    <UploadNicheItem
                      key={el.id}
                      img={el.thumbnail}
                      name={el.name}
                      subscribers={el.subscribers}
                      index={0}
                      id={el.id}
                      disabled={(selectedNiches.length >= 3 && !selectedNiches.find((n: any) => n === el.id))}
                      isSelected={selectedNiches.find((n: any) => n === el.id) ? true : false}
                      select={() => handleSelectNiche(el)}
                      profile_icon={profile_icon}
                      selected_icon={tick}
                    />
                  )) : <p>We have some issues with loading niches. Please close and open the dialog and try again.</p>}
                </NichesWrapper>
                <Button variant="contained" fullWidth sx={{
                  background: colors.purpleGradient,
                  marginTop: "2rem",
                  minHeight: "3rem",
                  borderRadius: "38px",
                  fontWeight: 500,
                }} disabled={selectedNiches.length < 1} onClick={onSubmit}>
                  {isLoading ? <CircularProgress size="2rem" sx={{ color: "white" }}/>
                    : <span>Add</span>
                  }
                </Button>
              </Fragment>
            )
        )
          : (
            <Fragment>
              <p>You’ve gotta log in to add to niches to RedGIFs!</p>
              <Button variant="contained" fullWidth sx={{
                background: colors.purpleGradient,
                margin: "2rem 0 0rem 0",
                minHeight: "3rem",
                borderRadius: "38px",
              }} onClick={handleLogin}>
                Login
              </Button>
            </Fragment>
          )
        }
      </Paper>
    </Backdrop>
  );
};

export default AddToNichePopup;

const PopupSubheader = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "24px",
  "& h3": {
    color: "black !important",
    fontSize: "20px",
    fontWeight: "500",
  },
  "& button": {
    padding: "6px 0",
    minWidth: "32px",
  },
  "& img": {
    width: "32px",
    height: "32px",
  },
});

const SearchContainer = styled("div")({
  "& img": {
    opacity: 0.5
  }
});

const InfoBlock = styled("div")({
  marginTop: "1rem",
  display: "flex",
  alignItems: "center",
  gap: "10px",
  "& p": {
    fontWeight: 500,
    fontSize: "12px",
    lineHeight: "150.02%",
    color: "#1D1D1D"
  }
});

const NichesWrapper = styled("div")({
  marginTop: "1rem",
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  width: "100%",
  overflowY: "auto",
  maxHeight: "50vh",
  "& > div": {
    width: "100%",
    padding: 0
  },
  "& div": {
    opacity: 1
  },
  "& > div div + div img": {
    width: "12px",
    position: "relative",
    top: "2px"
  },
  "& span": {
    opacity: 0.3
  }
});
